import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'

import { Card, Flex } from '@percent/lemonade'
import { useMutation, useQueryList } from '@percent/utility'
import { DomainAssigneesView } from '@percent/domain/iam'
import type { GroupedDomainAssignee, RoleAssignment } from '@percent/domain/iam'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { ErrorView, Loader } from '@percent/admin-dashboard/common/components'
import { useAuthState, useRequiredParams, useRolesAccess } from '@percent/admin-dashboard/common/hooks'
import { useBreadcrumbsData } from '@percent/admin-dashboard/app/Layout/breadcrumbsContext/useBreadcrumbsData/useBreadcrumbsData'
import { useAssigneeHref, useSearchAssignees } from '@percent/admin-dashboard/app/accounts/hooks'
import { useViewableDomains } from './useViewableDomains'

export function RoleDetailContainer() {
  const { domainId } = useRequiredParams<{ domainId: string }>()
  const { t } = useTranslation()
  const { iamService } = useServices()
  const { user } = useAuthState()
  const searchAssignees = useSearchAssignees()
  const getAssigneeHref = useAssigneeHref()
  const hasRolesAccess = useRolesAccess()
  const { setPath } = useBreadcrumbsData()

  const { domains, isLoading: isLoadingDomains, errorMessage: domainsErrorMessage } = useViewableDomains()
  const domain = domains?.find(({ id }) => id === domainId)

  useEffect(() => {
    setPath(domain?.name)

    return () => setPath(undefined)
  }, [domain?.name, setPath])

  const [
    { data: assignees, isLoading, isFetching, errorMessage, status },
    { nextPage, previousPage, refresh, refreshToFirstPage }
  ] = useQueryList(
    [`/authorisation/domains/${domainId}/assignees`],
    iamService.getDomainAssignees,
    { domainId },
    // An unknown id has no domain to show, so don't ask for its assignees
    { enabled: !!domain }
  )

  // The endpoint has no previous-page link, so after a change go back to the first page rather than
  // risk leaving the viewer on a now-empty later page; `refresh` covers already being on page 1.
  const handleAssigneesChanged = useCallback(() => {
    refreshToFirstPage()
    refresh()
  }, [refresh, refreshToFirstPage])

  const [, { mutateAsync: assignAccountRole }] = useMutation(iamService.assignAccountRole)
  const [, { mutateAsync: unassignAccountRole }] = useMutation(iamService.unassignAccountRole)

  const assignRole = ({ assigneeId, roleId }: RoleAssignment) =>
    assignAccountRole({ domainId, memberId: assigneeId, role: roleId })

  const unassignRole = ({ assigneeId, roleId }: RoleAssignment) =>
    unassignAccountRole({ domainId, memberId: assigneeId, role: roleId })

  // The API rejects removing your own roles, so don't offer it
  const canManageAssignee = (assignee: GroupedDomainAssignee) =>
    !(assignee.type === 'account' && assignee.id === user?.id)

  // Settled by now: the route's AccessRoute waited for permissions and its FeatureRoute for the flag
  if (!hasRolesAccess) {
    return <Navigate to="/accounts/members" replace />
  }

  if (isLoadingDomains) {
    return <Loader />
  }

  // A failed background refetch keeps the cached domain, so only an unresolved domain is an error
  if (!domain) {
    return <ErrorView errorMessage={domainsErrorMessage || t('rolesAndPermissions.error.domainNotFound')} />
  }

  return (
    <Card>
      <Flex direction="column" p="lg" w="100%">
        <DomainAssigneesView
          domain={domain}
          assignees={assignees}
          // useQueryList keeps the previous rows while refetching after a change, so treat any fetch
          // as loading
          isLoadingAssignees={isLoading || isFetching}
          assigneesErrorMessage={status === 'error' ? errorMessage : null}
          nextPage={nextPage}
          previousPage={previousPage}
          onAssigneesChanged={handleAssigneesChanged}
          assignRole={assignRole}
          unassignRole={unassignRole}
          searchAssignees={searchAssignees}
          getAssigneeHref={getAssigneeHref}
          canManageAssignee={canManageAssignee}
        />
      </Flex>
    </Card>
  )
}
